import React, { useState, useEffect } from "react";
import {
  getDatabase,
  ref,
  onValue,
  set,
  push,
  update,
  get,
} from "firebase/database";
import "./GeneralStudiesPage.css";

const GeneralStudiesPage = () => {
  const db = getDatabase();
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    question: "",
    answer: "",
    year: "",
    options: {
      0: "",
      1: "",
      2: "",
      3: "",
    },
  });
  const [newSubject, setNewSubject] = useState("");

  useEffect(() => {
    const subjectsRef = ref(db, "GeneralStudies/GeneralStudies/courses");
    onValue(subjectsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Get the course names (keys) instead of array indexes
        setSubjects(Object.keys(data));
      }
    });
  }, [db]);

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    const questionsRef = ref(
      db,
      `GeneralStudies/GeneralStudies/courses/${selectedSubject}/questions`
    );
    onValue(questionsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        // Map through questions while preserving their IDs
        setQuestions(Object.entries(data).map(([id, q]) => ({ id, ...q })));
      } else {
        setQuestions([]);
      }
    });
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject) {
      alert("Please enter a subject name.");
      return;
    }

    try {
      const subjectsRef = ref(db, "GeneralStudies/GeneralStudies/courses");
      await set(ref(subjectsRef, newSubject), {});
      alert("Subject added successfully.");
      setNewSubject("");
    } catch (error) {
      console.error("Error adding subject:", error);
      alert("Failed to add subject.");
    }
  };

  const handleEditSubject = async (oldSubject, newSubjectName) => {
    if (!newSubjectName) {
      alert("Please enter a new subject name.");
      return;
    }

    try {
      const oldSubjectRef = ref(
        db,
        `GeneralStudies/GeneralStudies/courses/${oldSubject}`
      );
      const newSubjectRef = ref(
        db,
        `GeneralStudies/GeneralStudies/courses/${newSubjectName}`
      );

      // Use get instead of onValue for one-time read
      const snapshot = await get(oldSubjectRef);
      if (snapshot.exists()) {
        await set(newSubjectRef, snapshot.val());
        await set(oldSubjectRef, null);
        alert("Subject updated successfully.");
      } else {
        alert("Subject not found.");
      }
    } catch (error) {
      console.error("Error updating subject:", error);
      alert("Failed to update subject.");
    }
  };
  const handleDeleteSubject = async (subject) => {
    try {
      const subjectRef = ref(
        db,
        `GeneralStudies/GeneralStudies/courses/${subject}`
      );
      await set(subjectRef, null);
      alert("Subject deleted successfully.");
    } catch (error) {
      console.error("Error deleting subject:", error);
      alert("Failed to delete subject.");
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (
      !selectedSubject ||
      !newQuestion.question ||
      !newQuestion.answer ||
      !newQuestion.year ||
      !newQuestion.options[0] ||
      !newQuestion.options[1] ||
      !newQuestion.options[2] ||
      !newQuestion.options[3]
    ) {
      alert("Please fill in all fields, including options.");
      return;
    }

    try {
      const questionsRef = ref(
        db,
        `GeneralStudies/fields/subjects/${selectedSubject}/questions`
      );
      const newQuestionRef = push(questionsRef);
      await set(newQuestionRef, newQuestion);
      alert("Question added successfully.");
      setNewQuestion({
        question: "",
        answer: "",
        year: "",
        options: { 0: "", 1: "", 2: "", 3: "" },
      });
    } catch (error) {
      console.error("Error adding question:", error);
      alert("Failed to add question.");
    }
  };

  const handleEditQuestion = async (id, updatedQuestion) => {
    try {
      const questionRef = ref(
        db,
        `GeneralStudies/fields/subjects/${selectedSubject}/questions/${id}`
      );
      await update(questionRef, updatedQuestion);
      alert("Question updated successfully.");
    } catch (error) {
      console.error("Error updating question:", error);
      alert("Failed to update question.");
    }
  };

  const handleDeleteQuestion = async (id) => {
    try {
      const questionRef = ref(
        db,
        `GeneralStudies/fields/subjects/${selectedSubject}/questions/${id}`
      );
      await set(questionRef, null); // Deletes the question
      alert("Question deleted successfully.");
    } catch (error) {
      console.error("Error deleting question:", error);
      alert("Failed to delete question.");
    }
  };

  return (
    <div className="general-studies-container">
      <h1 className="general-studies-title">General Studies Management</h1>
      <div className="subject-selector">
        <h2>Select a Subject</h2>
        <ul className="subject-list">
          {subjects.map((subject) => (
            <li
              key={subject}
              className={`subject-item ${
                selectedSubject === subject ? "selected" : ""
              }`}
              onClick={() => handleSubjectSelect(subject)}
            >
              {subject}
              {/* Add Edit and Delete buttons */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newName = prompt("Enter new subject name:", subject);
                  if (newName && newName !== subject) {
                    handleEditSubject(subject, newName);
                  }
                }}
                style={{ marginLeft: 8 }}
              >
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Delete subject "${subject}"?`)) {
                    handleDeleteSubject(subject);
                  }
                }}
                style={{ marginLeft: 4 }}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
        <form className="add-subject-form" onSubmit={handleAddSubject}>
          <h3>Add a New Subject</h3>
          <div className="form-group">
            <label>Subject Name:</label>
            <input
              type="text"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
            />
          </div>
          <button type="submit">Add Subject</button>
        </form>
      </div>

      {selectedSubject && (
        <div className="questions-section">
          <h2>Questions for {selectedSubject}</h2>
          <ul className="questions-list">
            {questions.map((q) => (
              <li key={q.id} className="question-item">
                <strong>Question:</strong> {q.question} <br />
                <strong>Answer:</strong> {q.answer} <br />
                <strong>Year:</strong> {q.year} <br />
                <strong>Options:</strong> <br />
                <ul>
                  {Object.entries(q.options).map(([key, value]) => (
                    <li key={key}>
                      <strong>Option {parseInt(key) + 1}:</strong> {value}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() =>
                    handleEditQuestion(q.id, {
                      question:
                        prompt("Edit question:", q.question) || q.question,
                      answer: prompt("Edit answer:", q.answer) || q.answer,
                      year: prompt("Edit year:", q.year) || q.year,
                    })
                  }
                >
                  Edit
                </button>
                <button onClick={() => handleDeleteQuestion(q.id)}>
                  Delete
                </button>
              </li>
            ))}
          </ul>

          <form className="add-question-form" onSubmit={handleAddQuestion}>
            <h3>Add a New Question</h3>
            <div className="form-group">
              <label>Question:</label>
              <input
                type="text"
                value={newQuestion.question}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, question: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Answer:</label>
              <input
                type="text"
                value={newQuestion.answer}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, answer: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Year:</label>
              <input
                type="text"
                value={newQuestion.year}
                onChange={(e) =>
                  setNewQuestion({ ...newQuestion, year: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Options:</label>
              <div className="options-group">
                <input
                  type="text"
                  placeholder="Option 1"
                  value={newQuestion.options[0]}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      options: { ...newQuestion.options, 0: e.target.value },
                    })
                  }
                />
                <input
                  type="text"
                  placeholder="Option 2"
                  value={newQuestion.options[1]}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      options: { ...newQuestion.options, 1: e.target.value },
                    })
                  }
                />
                <input
                  type="text"
                  placeholder="Option 3"
                  value={newQuestion.options[2]}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      options: { ...newQuestion.options, 2: e.target.value },
                    })
                  }
                />
                <input
                  type="text"
                  placeholder="Option 4"
                  value={newQuestion.options[3]}
                  onChange={(e) =>
                    setNewQuestion({
                      ...newQuestion,
                      options: { ...newQuestion.options, 3: e.target.value },
                    })
                  }
                />
              </div>
            </div>
            <button type="submit">Add Question</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default GeneralStudiesPage;
